import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, FormControlLabel,
  Button, TextField, Chip, CircularProgress, Alert, Collapse,
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Divider,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { satelliteAlertsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function SatellitePassAlerts() {
  const { user, requireAuth } = useAuth();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [locationName, setLocationName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | locating | done | error

  const activeSubscription = subscriptions.find(s => s.satelliteNoradId === 25544 && s.enabled);
  const isSubscribed = Boolean(activeSubscription);

  const fetchSubscriptions = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await satelliteAlertsAPI.mySubscriptions();
      setSubscriptions(res.data?.data || []);
    } catch {
      // silently fail on initial load
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const reverseGeocode = async (lat, lon) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      const data = await res.json();
      const addr = data.address || {};
      return addr.city || addr.town || addr.village || addr.county || addr.state || 'My Location';
    } catch {
      return 'My Location';
    }
  };

  const handleToggle = async () => {
    if (!requireAuth('Sign in to subscribe to ISS pass alerts')) return;

    if (isSubscribed) {
      setActionLoading(true);
      setError('');
      try {
        await satelliteAlertsAPI.unsubscribe();
        setSuccessMsg('Unsubscribed from ISS pass alerts.');
        await fetchSubscriptions();
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to unsubscribe.');
      } finally {
        setActionLoading(false);
      }
      return;
    }

    // Subscribe flow: get location then subscribe
    setGeoStatus('locating');
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      setGeoStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const name = locationName.trim() || await reverseGeocode(lat, lon);
        setLocationName(name);
        setGeoStatus('done');
        await doSubscribe(lat, lon, name);
      },
      () => {
        setError('Could not get your location. Please enter it manually below.');
        setGeoStatus('error');
        setEditingName(true);
      }
    );
  };

  const doSubscribe = async (lat, lon, name) => {
    setActionLoading(true);
    setError('');
    try {
      await satelliteAlertsAPI.subscribe({ latitude: lat, longitude: lon, locationName: name });
      setSuccessMsg(`You'll receive an email ~20 min before the ISS is visible over ${name} 🛰️`);
      setEditingName(false);
      await fetchSubscriptions();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to subscribe.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualSubscribe = async () => {
    if (!locationName.trim()) {
      setError('Please enter a location name.');
      return;
    }
    // Try geolocation one more time or use placeholder coords
    navigator.geolocation?.getCurrentPosition(
      async (pos) => doSubscribe(pos.coords.latitude, pos.coords.longitude, locationName.trim()),
      async () => {
        setError('Location access denied. Cannot subscribe without coordinates.');
      }
    );
  };

  const handleUnsubscribeById = async (id) => {
    setActionLoading(true);
    setError('');
    try {
      await satelliteAlertsAPI.unsubscribe();
      setSuccessMsg('Unsubscribed successfully.');
      await fetchSubscriptions();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to unsubscribe.');
    } finally {
      setActionLoading(false);
    }
  };

  if (!user) {
    return (
      <Card sx={{ mt: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <NotificationsActiveIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>ISS Pass Alerts</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Sign in to get email alerts ~20 minutes before the ISS passes over your location.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: isSubscribed ? 'primary.main' : 'divider' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isSubscribed
              ? <NotificationsActiveIcon sx={{ color: 'primary.main' }} />
              : <NotificationsOffIcon sx={{ color: 'text.disabled' }} />
            }
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                ISS Pass Alerts
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Email notification ~20 min before a visible pass
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSubscribed && (
              <Chip
                label={activeSubscription.locationName}
                size="small"
                icon={<LocationOnIcon />}
                color="primary"
                variant="outlined"
              />
            )}
            {(actionLoading || geoStatus === 'locating') ? (
              <CircularProgress size={22} />
            ) : (
              <Switch
                checked={isSubscribed}
                onChange={handleToggle}
                color="primary"
              />
            )}
          </Box>
        </Box>

        {/* Status messages */}
        <Collapse in={Boolean(successMsg)}>
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        </Collapse>
        <Collapse in={Boolean(error)}>
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Collapse>

        {/* Manual location input shown on geo error */}
        <Collapse in={editingName && !isSubscribed}>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
            <TextField
              size="small"
              label="Location name (e.g. Pune)"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === 'Enter' && handleManualSubscribe()}
            />
            <Button variant="contained" size="small" onClick={handleManualSubscribe} disabled={actionLoading}>
              Subscribe
            </Button>
          </Box>
        </Collapse>

        {/* My Subscriptions list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : subscriptions.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              My Subscriptions
            </Typography>
            <List dense disablePadding>
              {subscriptions.map((sub) => (
                <ListItem key={sub.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ISS over {sub.locationName}
                        </Typography>
                        <Chip
                          label={sub.enabled ? 'Active' : 'Paused'}
                          size="small"
                          color={sub.enabled ? 'success' : 'default'}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {sub.latitude?.toFixed(2)}°, {sub.longitude?.toFixed(2)}° · NORAD {sub.satelliteNoradId}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    {sub.enabled && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleUnsubscribeById(sub.id)}
                        disabled={actionLoading}
                        title="Unsubscribe"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
}
